import {Injectable} from '@angular/core';
import {BaseProvider} from '@providers/base.provider';
import {LaboratoryDocumentsService} from '@providers/laboratory-documents/laboratory-documents.service';
import {LaboratoryDocument} from '@models/laboratory-document';
import {LaboratoryDocumentsNamespace} from '@components/laboratory-documents/laboratory-documents.namespace';
import {LaboratoryDocumentsTypesProvider} from '@providers/laboratory-documents-types/laboratory-documents-types.provider';
import {AuthService} from '@services/auth.service';
import {UserNamespace} from '@namespaces/user.namespace';
import {map} from 'rxjs/operators';
import {Observable} from 'rxjs';

@Injectable()
export class LaboratoryDocumentsProvider extends BaseProvider<LaboratoryDocument[]> {
    public defaultValue = [];

    /**
     * Документы, относящиеся только к организации
     * * Все документы, кроме свидетельства об аттестации
     */
    public organizationDocuments$: Observable<LaboratoryDocument[]> = this.data$.pipe(
        map((list: LaboratoryDocument[]) => list.filter(item => item.type !== LaboratoryDocumentsNamespace.DocumentTypeEnum.attestation))
    );

    /**
     * Документы, относящиеся только к лаборатории
     * * Только свидетельство об аттестации
     */
    public laboratoryDocuments$: Observable<LaboratoryDocument[]> = this.data$.pipe(
        map((list: LaboratoryDocument[]) => list.filter(item => item.type === LaboratoryDocumentsNamespace.DocumentTypeEnum.attestation))
    );

    constructor(
        service: LaboratoryDocumentsService,
        private documentsTypesProvider: LaboratoryDocumentsTypesProvider,
        private authService: AuthService,
    ) {
        super(service);
        /**
         * Устанавливаем дефолтные значения
         */
        this.data$.next(this.defaultValue);
        this.updateData();

        /**
         * При изменении данных пользователя обновляем данные провайдера.
         */
        this.authService.user$.subscribe((user) => {
            /**
             * Если пользователь есть, значит был новый логин или изменение настроек профиля
             * и нужно обновить данные провайдера,
             * а если пользователя больше нет, значит он разлогинился и нужно очистить данные
             */
            if (user) {
                this.updateData();
            } else {
                this.clearData();
            }
        });
    }

    public checkPermissions(): boolean {
        return this.authService.hasPermissions([UserNamespace.Permission.structureUnitDocumentList]);
    }

    processedData(data: LaboratoryDocumentsNamespace.DocumentApiData[] = []): LaboratoryDocument[] {
        /**
         * На всякий случай будем отбрасывать все документы с неизвестными нам типами, так как frontend не умеет их обрабатывать
         */
        const types = this.documentsTypesProvider.data.map(item => item.id);
        const validData = data.filter(item => types.includes(item.type));

        return validData.map(item => {
            const docType = this.documentsTypesProvider.data.find(value => value.id === item.type);
            const name = item.type === LaboratoryDocumentsNamespace.DocumentTypeEnum.custom ? item.custom_name : docType.title;
            return new LaboratoryDocument(item, name);
        }).sort((a, b) => {
            /**
             * В пределах одного типа сортируем по номеру документа, так как документ с меньшим номеров физически раньше выдан,
             * чем документ с большим
             */
            if (a.type === b.type) {
                return a.number < b.number ? -1 : 1;
            }

            /**
             * Кастомные документы всегда идут в конце списка
             */
            if (a.type === LaboratoryDocumentsNamespace.DocumentTypeEnum.custom) {
                return 1;
            } else if (b.type === LaboratoryDocumentsNamespace.DocumentTypeEnum.custom) {
                return -1;
            }

            /**
             * Сортируем по типу документа
             */
            return a.type < b.type ? -1 : 1;
        });
    }
}
